import { useCallback, useState, useEffect, useMemo } from "react";
import { locale } from "src/config/app-config";
import type {
  HomePageSingleBrand,
  ImageBannerData,
  SummaryBrand,
} from "src/types/graphql.d";
import { CarouselHeader } from "src/molecules/CarouselHeader";
import { Carousel } from "src/molecules/Carousel";
import { BrandCard } from "src/molecules/BrandCard";
import { Box, BoxProps, Flex, useBreakpoints } from "@otrium/core";
import { EmotionBaseProps as BaseProps } from "src/types/global";
import { useImageHeight } from "src/hooks/useImageHeight";
import SegmentBrandTracking from "src/atoms/SegmentTracking/SegmentBrandTracking";
import { Brand, BrandInjectedProps, SEGMENT_PROMOTION_TYPE } from "src/segment";
import { filterExpiredImageBannerDeals } from "src/utils/filterExpiredImageBannerDeals";
import { LoginRegisterModal } from "../LoginRegisterModal";
import {
  GATED_BRAND_CAROUSEL_VIEW_ALL_SOURCE,
  GATED_POPUP_REDIRECT_DATA,
} from "src/constants/gatedItem";
import {
  deleteSessionStorageItem,
  setSessionStorageJSON,
} from "src/utils/helper/sessionStorage";
import { useIsClient } from "src/hooks/use-is-client";
import { Skeleton } from "src/atoms/Skeleton";
import { theme } from "src/theme";
import { trackCarouselViewAllSelectedEvent } from "src/tracking/events/carousel-view-all-selected";
import { trackCarouselViewedEvent } from "src/tracking/events/carousel-viewed";
import { CAROUSEL_CONTENT_TYPE } from "src/utils/constants";

interface BrandCarouselProps {
  title?: string;
  subTitle?: string;
  headerLinkHref?: string;
  headerLinkAs?: string;
  brands: Brand[] | null;
  genderId: string;
  /** Overrides the card aspect ratio (height / width) per device. */
  cardRatio?: { mobile: number; desktop: number };
  slidesDesktop?: number;
  slidesTablet?: number;
  slidesMobile?: number;
  orderByNew?: boolean;
  associatedCategoryId?: string | null;
  disableTranslation?: boolean;
  slideShadowSpace?: number;
  shouldReset?: boolean;
  tab?: string;
  hasVerticalPadding?: boolean;
  promotionType: SEGMENT_PROMOTION_TYPE;
  pageName?: string;
  isGated?: boolean;
  shopType?: string;
  homepagePosition?: number;
  setHasTimerEnded?: (hasTimerEnded: boolean) => void;
  carouselId?: string;
  carouselPosition?: number;
}

function buildPositionString({
  promotionType,
  index,
  homepagePosition,
}: {
  promotionType: SEGMENT_PROMOTION_TYPE;
  index: number;
  homepagePosition?: number;
}): string {
  return [
    promotionType,
    ...(homepagePosition ? [homepagePosition, index + 1] : [index + 1]),
  ].join("-");
}

function BrandCarousel({
  title,
  subTitle,
  headerLinkHref,
  headerLinkAs,
  brands,
  genderId,
  cardRatio,
  slidesDesktop,
  slidesTablet,
  slidesMobile,
  shouldReset,
  orderByNew = false,
  associatedCategoryId,
  disableTranslation = false,
  slideShadowSpace = 48,
  tab,
  hasVerticalPadding = true,
  promotionType,
  pageName,
  isGated,
  shopType,
  homepagePosition,
  carouselId,
  carouselPosition,
  setHasTimerEnded,
  ...rest
}: BoxProps & BaseProps & BrandCarouselProps): JSX.Element | null {
  const { isMobile } = useBreakpoints();
  const [open, setOpen] = useState<boolean>(false);
  const [imageElementWrapperHeight, getImageElementWrapper] = useImageHeight();
  const isClient = useIsClient();

  // Get the first available marketing block if timer is set on the same position
  const getFirstPerPosition = useCallback(
    (items: (ImageBannerData & { position?: string })[]) => {
      const seen = new Set();
      return items.filter((item) => {
        const pos = item.position;
        if (!pos || seen.has(pos)) return false;
        seen.add(pos);
        return true;
      });
    },
    []
  );

  const filteredBrands = useMemo(
    () => filterExpiredImageBannerDeals(brands as ImageBannerData[]),
    [brands]
  );

  const trackingData = useMemo(() => {
    if (
      title &&
      brands &&
      brands.length &&
      shopType &&
      carouselId &&
      carouselPosition !== undefined
    ) {
      return {
        title,
        subtitle: subTitle,
        tab,
        carouselId,
        carouselPosition,
        shopType,
        contentType: CAROUSEL_CONTENT_TYPE.BRAND,
        items: brands,
        targetLink: headerLinkHref,
        gated: !!isGated,
      };
    }

    return;
  }, [
    title,
    subTitle,
    tab,
    carouselId,
    carouselPosition,
    shopType,
    brands,
    headerLinkHref,
    isGated,
  ]);

  const handleCarouselNavigation = useCallback(
    (visibleIndexes: number[]) => {
      if (trackingData) {
        void trackCarouselViewedEvent({ ...trackingData, visibleIndexes });
      }
    },
    [trackingData]
  );

  const [brandsData, setBrandsData] = useState<Brand[]>(filteredBrands);

  useEffect(() => {
    let allBrands = filteredBrands || [];

    if (promotionType === SEGMENT_PROMOTION_TYPE.imageBanners) {
      allBrands = getFirstPerPosition(filteredBrands);
    } else if (filteredBrands.length > 6) {
      allBrands = filteredBrands.slice(0, 6);
    }

    setBrandsData(allBrands);
  }, [filteredBrands, promotionType, getFirstPerPosition]);

  const handleTimerExpired = useCallback(
    (uuid: string) => {
      const filtered =
        filterExpiredImageBannerDeals(brands as ImageBannerData[]).filter(
          (item) => item?.block_uuid !== uuid
        ) || [];

      const newBrandsData = getFirstPerPosition(filtered);

      setBrandsData(newBrandsData.length < 3 ? [] : newBrandsData);
    },
    [brands, getFirstPerPosition]
  );

  const handleViewAllClick = useCallback(() => {
    if (isGated) {
      setOpen(true);

      return;
    }

    if (trackingData) {
      void trackCarouselViewAllSelectedEvent({ ...trackingData });
    }
  }, [isGated, trackingData]);

  const setRedirectData = (type: string) => {
    deleteSessionStorageItem(GATED_POPUP_REDIRECT_DATA);
    setSessionStorageJSON(GATED_POPUP_REDIRECT_DATA, {
      gatedActionType: type,
      shopTypeFromRedirect: shopType,
      promo_type: `${promotionType as string}_${pageName as string}`,
      actionSource: GATED_BRAND_CAROUSEL_VIEW_ALL_SOURCE,
    });
  };

  const items = useMemo(
    () =>
      brandsData.map((brand, index) => {
        const { id } = brand as SummaryBrand | HomePageSingleBrand;
        const brandData = brand as ImageBannerData;

        return (
          <SegmentBrandTracking
            brand={brand}
            key={id || brandData?.block_uuid}
            position={buildPositionString({
              promotionType,
              index,
              homepagePosition,
            })}
            tab={tab}
            promotionType={promotionType}
            carouselTrackingData={trackingData}
            promotionDisplayType={brandData.display_type}
          >
            {({ onClickHandler }: BrandInjectedProps) => (
              <BrandCard
                associatedCategoryId={associatedCategoryId}
                brand={brand}
                genderId={genderId}
                ratio={cardRatio}
                getImageElementWrapper={getImageElementWrapper}
                locale={locale}
                orderByNew={orderByNew}
                onClickHandler={() => onClickHandler(id)}
                disableTranslation={disableTranslation}
                brandType={promotionType}
                refetchQueriesOnDeleteWishListBrand={[
                  "wishlistBrands",
                  "userWishlistMetaData",
                ]}
                position={buildPositionString({
                  promotionType,
                  index,
                  homepagePosition,
                })}
                tab={tab}
                handleTimerExpired={handleTimerExpired}
                pageName={pageName}
                shopType={shopType}
                setHasTimerEnded={setHasTimerEnded}
              />
            )}
          </SegmentBrandTracking>
        );
      }),
    [
      associatedCategoryId,
      brandsData,
      cardRatio,
      disableTranslation,
      genderId,
      getImageElementWrapper,
      handleTimerExpired,
      homepagePosition,
      orderByNew,
      pageName,
      promotionType,
      setHasTimerEnded,
      shopType,
      tab,
      trackingData,
    ]
  );

  if (brandsData.length < 3) {
    return null;
  }

  return (
    <Box as="section" {...rest}>
      {(title || subTitle) &&
        promotionType === SEGMENT_PROMOTION_TYPE.imageBanners && (
          <CarouselHeader
            title={title}
            subTitle={subTitle}
            href={isGated ? undefined : headerLinkHref}
            as={headerLinkAs}
            prefetch={false}
            isGated={isGated}
            viewAllOnClick={handleViewAllClick}
          />
        )}

      {!isClient && promotionType === SEGMENT_PROMOTION_TYPE.imageBanners ? (
        <Flex
          mx={`calc(${theme.space._8 || 8} * -1px)`}
          gap={theme.space.space24}
        >
          {Array.from(
            Array(slidesDesktop || slidesTablet || slidesMobile || 3).keys()
          ).map((_, index) => (
            <Box key={index} px={1} height="325px" mx={1} width="100%">
              <Skeleton />
            </Box>
          ))}
        </Flex>
      ) : null}

      {isClient ? (
        <Carousel
          shouldReset={shouldReset}
          slideShadowSpace={slideShadowSpace}
          isProductCarousel={false}
          slidesDesktop={slidesDesktop}
          slidesTablet={slidesTablet}
          slidesMobile={slidesMobile}
          marginSize={isMobile ? "m" : "l"}
          imageElementWrapperHeight={imageElementWrapperHeight}
          hasVerticalPadding={hasVerticalPadding}
          items={items}
          trackCarouselViewed={handleCarouselNavigation}
        />
      ) : null}

      {open && isClient ? (
        <LoginRegisterModal
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          redirectTo={headerLinkHref}
          trackingData={{
            pageName: pageName || "",
            shopType: shopType || genderId,
            actionSource: GATED_BRAND_CAROUSEL_VIEW_ALL_SOURCE,
            redirectTo: headerLinkHref,
          }}
          onRedirect={setRedirectData}
        />
      ) : null}
    </Box>
  );
}

export default BrandCarousel;
